import { useEffect, useState } from 'react';
import { parseISO, differenceInDays, startOfDay } from 'date-fns';

import {
  useGetAllChatsQuery,
  useGetChatMessagesQuery,
  useCreateNewChatMutation,
} from 'redux/services/borrower-profile';

import ChatHistoryModal from './ChatHistoryModal';
import styles from '@/styles/features/loan-information/chat-model/ChatHistory.module.css';

import { getUserDetails } from 'auth/auth';
import { useSelector } from 'react-redux';
import { userRoleData } from 'redux/slices/userRole';

const ChatHistory = ({
  isMaximized,
  setMessages,
  isVectorClicks,
  borrowerId,
  applicationId,
  setVectorClicks,
  setIsNewChat,
  setNewChatOpens,
  isNewChatOpens,
  chatHistorySelected,
  setChatHistorySelected,
  setNewChatSelected,
  onActivateSession,
  buildChatMetadata,
  setIsHistoryLoading,
}) => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [selectedChatID, setSelectedChatID] = useState(null);
  const [groupedChats, setGroupedChats] = useState([]);

  const { userId } = getUserDetails();
  const role = useSelector(userRoleData);
  const { data: sessionList, refetch: refetchAllChatHistory } = useGetAllChatsQuery(
    { userId },
    { skip: !role?.userRole || !userId }
  );

  const {
    data: getChatMessages,
    refetch: refetchMessages,
    isFetching: isMessagesFetching,
  } = useGetChatMessagesQuery(
    { sessionId: selectedChatID, userId },
    { skip: !selectedChatID || !userId }
  );

  useEffect(() => {
    setIsHistoryLoading?.(isMessagesFetching && !!selectedChatID);
  }, [isMessagesFetching, selectedChatID, setIsHistoryLoading]);

  // The header pencil starts a new chat without going through this panel, so
  // selectedChatID can still point at the chat that was open before it. Left
  // there, the hydration effect below pushes that chat's messages into the new
  // one on the next refetch. Leaving history mode drops the selection, which
  // also skips the query.
  useEffect(() => {
    if (!chatHistorySelected) {
      setSelectedChatID(null);
    }
  }, [chatHistorySelected]);

  const [createNewChat] = useCreateNewChatMutation();

  //To change the response message structure to expected based on existing chatbot
  useEffect(() => {
    const messageList = getChatMessages?.messages ?? (Array.isArray(getChatMessages) ? getChatMessages : []);
    if (messageList.length > 0) {
      const transformedMessages = messageList.map((message) => {
        const sender = message?.role?.toLowerCase() === 'user' ? 'user' : 'AI';
        let content = message?.content ?? '';
        if (typeof content === 'string') {
          const trimmed = content.trim();
          if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
            try {
              const parsed = JSON.parse(trimmed);
              const maybeMessage = parsed?.data?.message ?? parsed?.message;
              content = typeof maybeMessage === 'string' ? maybeMessage : trimmed;
            } catch (error) {
              // keep original string
            }
          }
        } else {
          content = JSON.stringify(content);
        }
        // The SPEECH section is deliberately LEFT IN PLACE here — the render
        // strips it for display and hands the text to the speaker button.
        // See the matching note in chat-model/index.jsx.
        // Keep id/feedback/versions so message actions and the version slider
        // hydrate on history selection (mirrors the parent transformHistoryMessages).
        const versions = message?.versions || null;
        return {
          sender,
          content: versions && versions.length ? versions[versions.length - 1] : content,
          isFromHistory: true,
          created_at: message?.created_at,
          id: message?.id,
          feedback: message?.feedback ?? null,
          /* This is the path a MONTH-OLD session comes back through, so a
             guided answer that loses its flow here reverts to prose exactly
             where the reader expects the card. */
          artifacts: message?.artifacts || null,
          guidedFlow: message?.guided_flow || null,
          versions,
          activeVersion: versions && versions.length ? versions.length - 1 : 0,
        };
      });
      setMessages(transformedMessages);
    }
  }, [getChatMessages, setMessages]);

  useEffect(() => {
    if (isVectorClicks) {
      onChatHistoryClicks();
    }
  }, [isVectorClicks]);

  useEffect(() => {
    if (!isVectorClicks) {
      setIsChatOpen(false);
      setNewChatOpens(false);
    }
  }, [isVectorClicks, setNewChatOpens]);

  // Update grouped chats when sessions change
  useEffect(() => {
    const chats = sessionList?.chats ?? (Array.isArray(sessionList) ? sessionList : sessionList?.data ?? []);
    setGroupedChats(groupChatsByRelativeTime(chats));
  }, [sessionList]);

  //Function to group the chats by date
  const getTimestamp = (chat) =>
    chat?.last_message_at ?? chat?.metadata?.last_message_at ?? chat?.created_at;

  const groupChatsByRelativeTime = (chats) => {
    const now = startOfDay(new Date());
    const groupedChats = {
      Today: [],
      Yesterday: [],
      Earlier: [],
    };
    const sortedChats = Array.isArray(chats)
      ? [...chats].sort((a, b) => {
          const tsA = getTimestamp(a);
          const tsB = getTimestamp(b);
          const dateA = tsA ? new Date(tsA).getTime() : 0;
          const dateB = tsB ? new Date(tsB).getTime() : 0;
          return dateB - dateA;
        })
      : [];
    sortedChats.forEach((chat) => {
      const timestamp = getTimestamp(chat);
      const createdAt = timestamp ? parseISO(timestamp) : null;
      const chatDate = createdAt ? startOfDay(createdAt) : null;
      if (!chatDate) {
        groupedChats.Earlier.push(chat);
        return;
      }
      const daysDifference = differenceInDays(now, chatDate);
      if (daysDifference === 0) {
        groupedChats.Today.push(chat);
      } else if (daysDifference === 1) {
        groupedChats.Yesterday.push(chat);
      } else {
        groupedChats.Earlier.push(chat);
      }
    });
    return groupedChats;
  };

  //Function that triggers when the Chat History button clicks
  const onChatHistoryClicks = async () => {
    setNewChatSelected(false);
    setNewChatOpens(true);
    setIsChatOpen(true);
    try {
      const { data } = await refetchAllChatHistory();
      const sessions = data?.chats ?? (Array.isArray(data) ? data : data?.data ?? []);
      const updatedGroupedChats = groupChatsByRelativeTime(sessions);
      setGroupedChats(updatedGroupedChats);
    } catch (error) {
      console.error('Error fetching chat history:', error);
    }
  };

  //Function that triggers when selected a particular chat
  const setChatWindowOpens = (sessionId) => {
    setChatHistorySelected(true);
    // Show skeleton immediately — RTK Query's isFetching stays false on cache
    // hits, so relying on it alone makes the skeleton flicker or never appear.
    setIsHistoryLoading?.(true);
    setSelectedChatID(sessionId);
    refetchMessages();
    setIsChatOpen(false);
    setVectorClicks(false);
    setNewChatOpens(false);
    setMessages([]);
    if (sessionId) {
      onActivateSession?.(sessionId, {
        broadcast: true,
        resetMessages: true,
      });
    }
  };

  //function that triggers when the New Chat clicks
  const newChatAction = async () => {
    setIsNewChat(true);
    setIsChatOpen(false);
    setVectorClicks(false);
    setMessages([]);
    setChatHistorySelected(false);

    const metadataPayload =
      typeof buildChatMetadata === 'function'
        ? buildChatMetadata()
        : {
            source: 'chat-ui',
            initiatedAt: new Date().toISOString(),
          };

    const response = await createNewChat({
      borrower_id: borrowerId,
      loan_application_id: applicationId,
      metadata: metadataPayload,
    });
    const chatId = response?.data?.id ?? response?.id ?? null;
    if (chatId) {
      setSelectedChatID(chatId);
      onActivateSession?.(chatId, {
        broadcast: true,
        resetMessages: true,
      });
      await refetchAllChatHistory();
    } else {
      // REST failed (e.g. 401/503) — fall back to WebSocket session creation.
      // Sending null tells the backend to create a brand-new session.
      onActivateSession?.(null, { broadcast: true, resetMessages: true });
    }
  };

  const isPanelVisible = isVectorClicks && isChatOpen;
  const isHistoryLoadingFlag = isMessagesFetching && !!selectedChatID;

  return (
    <>
      {isPanelVisible && (
        <div className={`${styles.sidebar} ${isPanelVisible ? styles.open : ''}`}>
          <ChatHistoryModal
            isChatOpen={isChatOpen}
            isVectorClicks={isVectorClicks}
            setIsChatOpen={setIsChatOpen}
            newChatAction={newChatAction}
            setChatWindowOpens={setChatWindowOpens}
            groupedChats={groupedChats}
            setVectorClicks={setVectorClicks}
            setNewChatOpens={setNewChatOpens}
            setNewChatSelected={setNewChatSelected}
            isHistoryLoading={isHistoryLoadingFlag}
          />
        </div>
      )}
    </>
  );
};

export default ChatHistory;
